import { useBaaSApiHealth, ApiHealthStatus } from '@/hooks/useBaaSApiHealth';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Activity } from 'lucide-react';

const statusConfig: Record<ApiHealthStatus, { label: string; className: string; dot: string }> = {
  green: {
    label: 'API Healthy',
    className: 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
    dot: 'bg-emerald-500',
  },
  amber: {
    label: 'API Degraded',
    className: 'border-amber-500/30 text-amber-600 dark:text-amber-400',
    dot: 'bg-amber-500',
  },
  red: {
    label: 'API Down',
    className: 'border-destructive/30 text-destructive',
    dot: 'bg-destructive',
  },
  unknown: {
    label: 'API Status Unknown',
    className: 'border-border text-muted-foreground',
    dot: 'bg-muted-foreground',
  },
};

export function BaaSApiHealthBadge() {
  const { data: health, isLoading } = useBaaSApiHealth();

  const status = health?.status || 'unknown';
  const config = statusConfig[status];

  if (isLoading) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-border text-muted-foreground">
        <Activity className="w-3 h-3 animate-pulse" />
        Checking...
      </Badge>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`text-[10px] gap-1.5 cursor-default ${config.className}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${config.dot} ${status === 'amber' ? 'animate-pulse' : ''}`} />
            {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <p>{config.label}</p>
          {health?.failure_count ? (
            <p className="text-muted-foreground">Failures: {health.failure_count}</p>
          ) : null}
          {health?.last_check_at && (
            <p className="text-muted-foreground">
              Last check: {new Date(health.last_check_at).toLocaleTimeString()}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
